import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";

export type FoundryTracingMode = "project" | "account" | "skip";
const FOUNDRY_TRACING_MODES: readonly FoundryTracingMode[] = ["project", "account", "skip"];

/** Validate the optional --foundry-tracing value; undefined → undefined (bicep default applies). */
export function parseFoundryTracingMode(v: string | undefined): FoundryTracingMode | undefined {
  if (v === undefined) return undefined;
  if (!FOUNDRY_TRACING_MODES.includes(v as FoundryTracingMode)) {
    throw new LocalCliError({
      code: "DEPLOY_INVALID_TRACING_MODE",
      message: `Invalid --foundry-tracing value: '${v}'.`,
      hint: `Use one of: ${FOUNDRY_TRACING_MODES.join(", ")}.`,
    });
  }
  return v as FoundryTracingMode;
}

export interface BicepParams {
  location: string;
  suffix: string;
  imageRef: string;
  tenantId: string;
  clientId: string;
  foundryResourceId: string;
  foundryProjectEndpoint: string;
  acrPullIdentityResourceId: string;
  acrResourceId: string;
  foundryTracingMode?: FoundryTracingMode;
  provisionUpdater?: boolean;
  installerImage?: string;
  updateCron?: string;
  channelUrl?: string;
  /** When false, main.bicep SKIPS its subscription-scoped module(s). The RG-scoped
   *  updater MI cannot deploy at subscription scope, so its converge sets this off;
   *  the founder deploy/CLI (with sub-scope) leaves it default true. */
  assignSubscriptionRoles?: boolean;
  // Referee (E2-F5) — all optional; omitted ⇒ the bicep default applies (dormant:
  // gatewayCpu='0.25', gatewayMemory='0.5Gi', every referee/exam var=''). Only
  // set these when explicitly enabling the referee exam stack at deploy time.
  gatewayCpu?: string;
  gatewayMemory?: string;
  refereeEnabled?: string;
  refereeBrainRepos?: string;
  refereeFeedRepo?: string;
  refereeInstallationId?: string;
  refereeWebhookHmacKvUri?: string;
  examKvUri?: string;
  examLaWorkspaceId?: string;
  brainEvalDeployment?: string;
  brainAppLogin?: string;
  refereeCheckpointDir?: string;
  examApiBase?: string;
}

export interface BicepOutputs {
  containerAppFqdn: string;
  resourceNames: Partial<Record<string, string>>;
}

/** Build the `key=value` --parameters array for main.bicep (order is stable). */
export function buildBicepParams(p: BicepParams): string[] {
  const params = [
    `location=${p.location}`,
    `suffix=${p.suffix}`,
    `imageRef=${p.imageRef}`,
    `tenantId=${p.tenantId}`,
    `clientId=${p.clientId}`,
    `foundryResourceId=${p.foundryResourceId}`,
    `foundryProjectEndpoint=${p.foundryProjectEndpoint}`,
    `acrPullIdentityResourceId=${p.acrPullIdentityResourceId}`,
    `acrResourceId=${p.acrResourceId}`,
  ];
  if (p.foundryTracingMode) params.push(`foundryTracingMode=${p.foundryTracingMode}`);
  if (p.assignSubscriptionRoles !== undefined) params.push(`assignSubscriptionRoles=${String(p.assignSubscriptionRoles)}`);
  if (p.provisionUpdater !== undefined) params.push(`provisionUpdater=${String(p.provisionUpdater)}`);
  if (p.installerImage !== undefined) params.push(`installerImage=${p.installerImage}`);
  if (p.updateCron !== undefined) params.push(`updateCron=${p.updateCron}`);
  if (p.channelUrl !== undefined) params.push(`channelUrl=${p.channelUrl}`);
  // Referee (E2-F5): only emit params that were explicitly provided. Omitting
  // all of them (the default) reproduces today's exact param set — the CLI-layer
  // dormancy guarantee (see deploy.test.ts).
  if (p.gatewayCpu) params.push(`gatewayCpu=${p.gatewayCpu}`);
  if (p.gatewayMemory) params.push(`gatewayMemory=${p.gatewayMemory}`);
  if (p.refereeEnabled) params.push(`refereeEnabled=${p.refereeEnabled}`);
  if (p.refereeBrainRepos) params.push(`refereeBrainRepos=${p.refereeBrainRepos}`);
  if (p.refereeFeedRepo) params.push(`refereeFeedRepo=${p.refereeFeedRepo}`);
  if (p.refereeInstallationId) params.push(`refereeInstallationId=${p.refereeInstallationId}`);
  if (p.refereeWebhookHmacKvUri) params.push(`refereeWebhookHmacKvUri=${p.refereeWebhookHmacKvUri}`);
  if (p.examKvUri) params.push(`examKvUri=${p.examKvUri}`);
  if (p.examLaWorkspaceId) params.push(`examLaWorkspaceId=${p.examLaWorkspaceId}`);
  if (p.brainEvalDeployment) params.push(`brainEvalDeployment=${p.brainEvalDeployment}`);
  if (p.brainAppLogin) params.push(`brainAppLogin=${p.brainAppLogin}`);
  if (p.refereeCheckpointDir) params.push(`refereeCheckpointDir=${p.refereeCheckpointDir}`);
  if (p.examApiBase) params.push(`examApiBase=${p.examApiBase}`);
  return params;
}

/** Resolve the repo checkout dir from the ~/.m8t-stack/repo-root marker. */
export async function resolveRepoRoot(home: string = os.homedir()): Promise<string> {
  const marker = path.join(home, ".m8t-stack", "repo-root");
  try {
    const root = (await fs.readFile(marker, "utf8")).trim();
    if (!root) throw new Error("empty");
    return root;
  } catch {
    throw new LocalCliError({
      code: "DEPLOY_NO_REPO_ROOT",
      message: "~/.m8t-stack/repo-root marker is missing or empty.",
      hint: "Run 'm8t install' from an m8t-stack checkout (it writes the marker), or run 'm8t deploy' from the repo.",
    });
  }
}

/** Resolve the Foundry account ARM id from an explicit value or the endpoint. */
export async function resolveFoundryResourceId(
  endpoint: string,
  explicit?: string,
): Promise<string> {
  if (explicit) return explicit;
  const m = /^https:\/\/([^.]+)\.services\.ai\.azure\.com/.exec(endpoint);
  if (!m) {
    throw new LocalCliError({
      code: "DEPLOY_FOUNDRY_ENDPOINT_BAD",
      message: `Cannot parse the Foundry account name from endpoint: ${endpoint}`,
      hint: "Pass --foundry-resource-id <ARM-id> explicitly.",
    });
  }
  const accounts = JSON.parse(
    await runAz([
      "cognitiveservices", "account", "list",
      "--query", `[?name=='${m[1]}'].id`,
      "--output", "json",
    ]),
  ) as string[];
  if (accounts.length === 0) {
    throw new LocalCliError({
      code: "DEPLOY_FOUNDRY_NOT_FOUND",
      message: `No Foundry account named '${m[1]}' in the active subscription.`,
      hint: "Check the endpoint, or pass --foundry-resource-id explicitly.",
    });
  }
  if (accounts.length > 1) {
    throw new LocalCliError({
      code: "DEPLOY_FOUNDRY_AMBIGUOUS",
      message: `Multiple Foundry accounts named '${m[1]}' — ambiguous.`,
      hint: "Pass --foundry-resource-id <ARM-id> to disambiguate.",
    });
  }
  return accounts[0];
}

/**
 * Resolve the ACR ARM resource id for a private-ACR imageRef — needed to scope
 * the AcrPull role assignment when the Bicep provisions the pull identity.
 * Returns '' for public (non-*.azurecr.io) images so the public path is
 * preserved. An explicit --acr-resource-id wins (e.g. a custom login server the
 * host label can't resolve). Throws DEPLOY_ACR_NOT_FOUND when `az acr show`
 * can't find the registry.
 */
export async function resolveAcrResourceId(opts: {
  imageRef: string;
  explicit?: string;
}): Promise<string> {
  if (opts.explicit) return opts.explicit;
  const host = opts.imageRef.split("/")[0];
  if (!host.endsWith(".azurecr.io")) return "";
  const acrName = host.split(".")[0];
  try {
    const id = (await runAz(["acr", "show", "-n", acrName, "--query", "id", "-o", "tsv"])).trim();
    if (!id) throw new Error("empty id");
    return id;
  } catch (e) {
    if (e instanceof LocalCliError) throw e;
    throw new LocalCliError({
      code: "DEPLOY_ACR_NOT_FOUND",
      message: `Cannot resolve the ACR '${acrName}' for image ${opts.imageRef}.`,
      hint: "Create it (az acr create), sign in (az acr login), and push the web image (docker buildx … --push); or pass --acr-resource-id <ARM-id>.",
    });
  }
}

/** Ensure the resource group exists (create with a deployment tag if not). */
export async function ensureResourceGroup(name: string, location: string): Promise<void> {
  const existing = JSON.parse(
    (await runAz(["group", "show", "--name", name, "--output", "json"]).catch(() => "")) || "null",
  ) as Record<string, unknown> | null;
  if (existing) return;
  await runAz(["group", "create", "--name", name, "--location", location, "--tags", "m8t-stack=deployment"]);
}

/**
 * Resolve an explicit AcrPull UA identity override (--acrpull-identity). When
 * empty, returns '' and the Bicep provisions the identity itself (see
 * `provisionAcrPull` in deploy/main.bicep). Pass an external identity id to
 * reference it and skip provisioning (BYO-identity).
 */
export function resolveAcrPullIdentity(opts: { explicit?: string }): string {
  return opts.explicit ?? "";
}

/** Run `az deployment group create` against <repoRoot>/deploy/main.bicep. */
export async function runBicepDeployment(opts: {
  resourceGroup: string;
  repoRoot: string;
  params: string[];
  deploymentName: string;
}): Promise<BicepOutputs> {
  const bicepPath = path.join(opts.repoRoot, "deploy", "main.bicep");
  const result = JSON.parse(
    await runAz([
      "deployment", "group", "create",
      "--name", opts.deploymentName,
      "--resource-group", opts.resourceGroup,
      "--template-file", bicepPath,
      "--parameters", ...opts.params,
      "--output", "json",
    ]),
  ) as { properties?: { outputs?: Partial<Record<string, { value: unknown }>> } };
  const outputs = result.properties?.outputs ?? {};
  const fqdn = outputs.containerAppFqdn?.value;
  if (typeof fqdn !== "string" || !fqdn) {
    throw new LocalCliError({
      code: "DEPLOY_NO_FQDN",
      message: "Bicep deployment did not return containerAppFqdn.",
      hint: "Check deploy/main.bicep outputs and the deployment log.",
    });
  }
  return {
    containerAppFqdn: fqdn,
    resourceNames: (outputs.resourceNames?.value as Record<string, string> | undefined) ?? {},
  };
}
